import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DataError,
  IOption,
  LoadingIndicator,
  Select
} from "@digital-services/ui";
import { isNotEmpty, useInput } from "@ds-cps/ui";

import {
  ApplicationJsonApiResponse,
  ApplicationJsonModel
} from "@application/model/ApplicationJsonModel";
import { useGetApplications } from "@application/services/use-get-applications";
import { useGetOrganizationApplications } from "@organization/services/use-get-organization-applications";

interface Props {
  label: string;
  selectedApplicationId: string | undefined;
  disabled?: boolean;
  organizationId: string;
  onInputChange?: (newValue: string, isValid: boolean) => void;
}

const mapperFunc = ({ id, name, appEnv }: Partial<ApplicationJsonModel>) => {
  let labelValue = name!;

  if (appEnv) {
    labelValue += ` (${appEnv})`;
  }

  return {
    value: id!,
    label: labelValue
  };
};

const ApplicationDropDown: FC<Props> = ({
  label,
  selectedApplicationId,
  disabled = false,
  organizationId,
  onInputChange
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "organization.applications"
  });
  const { t: tCommon } = useTranslation();

  const [options, setOptions] = useState<IOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<IOption | null>(null);

  const {
    applications: applicationsSimplified,
    isLoading,
    error
  } = useGetApplications();

  const {
    associatedApplications,
    isLoading: isApplicationsLoading,
    error: isApplicationsError
  } = useGetOrganizationApplications(organizationId);

  const { isValid, errorMessage, valueChangeHandler } = useInput(
    [
      isNotEmpty(() =>
        tCommon("validation.string.not-empty", { fieldName: label })
      )
    ],
    selectedApplicationId
  );

  useEffect(() => {
    const filterFunc = (item: ApplicationJsonModel): boolean => {
      const filteredApps: ApplicationJsonApiResponse[] | undefined =
        associatedApplications?.filter(
          application => application.data.id === item.id
        );

      return filteredApps?.length === 0;
    };

    if (applicationsSimplified) {
      if (disabled) {
        setOptions(applicationsSimplified.map(mapperFunc));
      } else {
        setOptions(applicationsSimplified.filter(filterFunc).map(mapperFunc));
      }
    }
  }, [applicationsSimplified, associatedApplications, disabled]);

  useEffect(() => {
    if (options.length > 0) {
      if (selectedApplicationId) {
        const foundOption = options.find(
          option => option.value.toString() === selectedApplicationId
        );
        setSelectedOption(foundOption ?? null);
      }
    }
  }, [selectedApplicationId, options]);

  const handleChange = (option: IOption) => {
    setSelectedOption(option);
    const { value, isValid } = valueChangeHandler(option.value as string);
    if (onInputChange) onInputChange(value, isValid);
  };

  if (isLoading || isApplicationsLoading) {
    return (
      <div className="flex w-full items-center text-base">
        <LoadingIndicator label={t("load.message")} />
      </div>
    );
  }

  if (error || isApplicationsError) {
    return (
      <DataError
        title={t("load.error.message")}
        message=""
        className="text-left! text-red-600"
      />
    );
  }

  const noData =
    applicationsSimplified === undefined ||
    applicationsSimplified.length === 0 ||
    options.length === 0;

  if (noData) {
    return (
      <div data-testid="application-no-data">{t("load.none.message")}</div>
    );
  }

  return (
    <Select
      data-testid="application"
      disabled={disabled}
      error={!isValid}
      label={label}
      placeholder={label}
      helperText={isValid ? undefined : errorMessage}
      key="application"
      options={options}
      selected={selectedOption}
      onChange={handleChange}
    />
  );
};

export default ApplicationDropDown;
